import { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react';
import { fieldDatabase } from '@/shared/db/default-field-database';
import { cleanupSyncedSessionPhotoBlobs } from '@/shared/db/photo-blob-cleanup';
import { CaptureSessionRepository } from '@/shared/db/repositories/capture-session-repository';
import { FloorplanBlobRepository } from '@/shared/db/repositories/floorplan-blob-repository';
import { MeterSearchRepository } from '@/shared/db/repositories/meter-search-repository';
import { PhotoBlobRepository } from '@/shared/db/repositories/photo-blob-repository';
import { SyncQueueRepository } from '@/shared/db/repositories/sync-queue-repository';
import { type FieldDatabase, SYNC_QUEUE_STATUSES } from '@/shared/db/schema';

export interface InventoryAppServices {
  captureSessions: CaptureSessionRepository;
  database: FieldDatabase;
  floorplanBlobs: FloorplanBlobRepository;
  meterSearch: MeterSearchRepository;
  photoBlobs: PhotoBlobRepository;
  syncQueue: SyncQueueRepository;
}

const InventoryAppServicesContext = createContext<InventoryAppServices | null>(null);

export function InventoryAppServicesProvider({
  children,
  services,
}: {
  children: ReactNode;
  services?: InventoryAppServices;
}) {
  const defaultServices = useMemo(() => services ?? createInventoryAppServices(), [services]);

  useEffect(() => {
    void Promise.all([
      defaultServices.database.getAll('capture_sessions'),
      defaultServices.database.getAll('sync_queue'),
    ])
      .then(([sessions, syncQueue]) => {
        const syncedSessionIds = new Set(
          syncQueue.filter(record => record.status === SYNC_QUEUE_STATUSES.synced).map(record => record.sessionId),
        );

        return cleanupSyncedSessionPhotoBlobs(
          defaultServices.photoBlobs,
          sessions.filter(session => syncedSessionIds.has(session.id)),
        );
      })
      .catch(() => undefined);
  }, [defaultServices]);

  return (
    <InventoryAppServicesContext.Provider value={defaultServices}>{children}</InventoryAppServicesContext.Provider>
  );
}

export function useInventoryAppServices() {
  const services = useContext(InventoryAppServicesContext);

  if (!services) {
    throw new Error('useInventoryAppServices must be used inside InventoryAppServicesProvider.');
  }

  return services;
}

function createInventoryAppServices(): InventoryAppServices {
  return {
    captureSessions: new CaptureSessionRepository(fieldDatabase),
    database: fieldDatabase,
    floorplanBlobs: new FloorplanBlobRepository(fieldDatabase),
    meterSearch: new MeterSearchRepository(fieldDatabase),
    photoBlobs: new PhotoBlobRepository(fieldDatabase),
    syncQueue: new SyncQueueRepository(fieldDatabase),
  };
}
